'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

type SignUpFormData = {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
  agreeToTerms: boolean;
};

interface SignUpFormProps {
  onSwitchToLogin: () => void;
}

const DOMAINS = ['mailnova.io', 'dropbox.email', 'fastinbox.net'];

export default function SignUpForm({ onSwitchToLogin }: SignUpFormProps) {
  const router = useRouter();
  const { signUp } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState(DOMAINS[0]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SignUpFormData>();

  const passwordValue = watch('password', '');

  const onSubmit = async (data: SignUpFormData) => {
    setIsLoading(true);
    try {
      await signUp(data.email, data.password, {
        fullName: data.fullName,
        preferredDomain: selectedDomain,
      });
      toast.success('Account created — redirecting to your inbox');
      router.push('/inbox-dashboard');
      router.refresh();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to create account. Please try again.');
      setIsLoading(false);
    }
  };

  const passwordStrength = (() => {
    if (!passwordValue) return { score: 0, label: '', color: '' };
    let score = 0;
    if (passwordValue.length >= 8) score++;
    if (/[A-Z]/.test(passwordValue)) score++;
    if (/[0-9]/.test(passwordValue)) score++;
    if (/[^A-Za-z0-9]/.test(passwordValue)) score++;
    const map = [
      { score: 0, label: '', color: '' },
      { score: 1, label: 'Weak', color: 'bg-danger' },
      { score: 2, label: 'Fair', color: 'bg-warning' },
      { score: 3, label: 'Good', color: 'bg-primary' },
      { score: 4, label: 'Strong', color: 'bg-success' },
    ];
    return map[score];
  })();

  return (
    <div className="animate-fade-in">
      <h2 className="text-2xl font-bold text-foreground mb-1">Create your account</h2>
      <p className="text-muted-foreground text-sm mb-8">
        Free forever. Upgrade when you need a permanent address.
      </p>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
        <div>
          <label htmlFor="full-name" className="block text-sm font-medium text-foreground mb-1.5">Full name</label>
          <input id="full-name" type="text" className="input-field" placeholder="Alex Mercer"
            {...register('fullName', { required: 'Full name is required', minLength: { value: 2, message: 'At least 2 characters' } })} />
          {errors.fullName && <p className="mt-1.5 text-xs text-danger">{errors.fullName.message}</p>}
        </div>

        <div>
          <label htmlFor="signup-email" className="block text-sm font-medium text-foreground mb-1.5">Email address</label>
          <input id="signup-email" type="email" className="input-field" placeholder="your@email.com"
            {...register('email', { required: 'Email is required', pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' } })} />
          {errors.email && <p className="mt-1.5 text-xs text-danger">{errors.email.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">Preferred inbox domain</label>
          <div className="flex gap-2 flex-wrap">
            {DOMAINS.map((d) => (
              <button key={`domain-${d}`} type="button" onClick={() => setSelectedDomain(d)}
                className={`px-3 py-1.5 rounded-lg text-sm font-mono font-medium border transition-all duration-150 ${
                  selectedDomain === d ? 'bg-primary/10 border-primary/50 text-primary' : 'bg-muted border-border text-muted-foreground hover:text-foreground'
                }`}>
                @{d}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label htmlFor="signup-password" className="block text-sm font-medium text-foreground mb-1.5">Password</label>
          <div className="relative">
            <input id="signup-password" type={showPassword ? 'text' : 'password'} className="input-field pr-11" placeholder="Min. 8 characters"
              {...register('password', { required: 'Password is required', minLength: { value: 8, message: 'Minimum 8 characters' } })} />
            <button type="button" onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
              {showPassword ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
                  <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>
          {passwordValue && (
            <div className="mt-2">
              <div className="flex gap-1 mb-1">
                {[1, 2, 3, 4].map((i) => (
                  <div key={`strength-${i}`} className={`h-1 flex-1 rounded-full transition-all duration-300 ${i <= passwordStrength.score ? passwordStrength.color : 'bg-border'}`} />
                ))}
              </div>
              {passwordStrength.label && (
                <p className="text-xs text-muted-foreground">Strength: <span className="font-semibold">{passwordStrength.label}</span></p>
              )}
            </div>
          )}
          {errors.password && <p className="mt-1.5 text-xs text-danger">{errors.password.message}</p>}
        </div>

        <div>
          <label htmlFor="confirm-password" className="block text-sm font-medium text-foreground mb-1.5">Confirm password</label>
          <input id="confirm-password" type="password" className="input-field" placeholder="Repeat your password"
            {...register('confirmPassword', { required: 'Please confirm your password', validate: (val) => val === passwordValue || 'Passwords do not match' })} />
          {errors.confirmPassword && <p className="mt-1.5 text-xs text-danger">{errors.confirmPassword.message}</p>}
        </div>

        <div className="flex items-start gap-2.5 pt-1">
          <input id="agree-terms" type="checkbox" className="w-4 h-4 mt-0.5 rounded border-border bg-input accent-primary cursor-pointer flex-shrink-0"
            {...register('agreeToTerms', { required: 'You must accept the terms' })} />
          <label htmlFor="agree-terms" className="text-sm text-secondary-foreground cursor-pointer leading-snug">
            I agree to the <a href="#" className="text-primary hover:underline">Terms of Service</a> and <a href="#" className="text-primary hover:underline">Privacy Policy</a>
          </label>
        </div>
        {errors.agreeToTerms && <p className="text-xs text-danger">{errors.agreeToTerms.message}</p>}

        <button type="submit" className="btn-primary w-full mt-2" disabled={isLoading}>
          {isLoading ? (
            <>
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Creating account...
            </>
          ) : 'Create free account'}
        </button>
      </form>

      <p className="mt-6 text-center text-sm text-muted-foreground">
        Already have an account?{' '}
        <button type="button" onClick={onSwitchToLogin} className="text-primary font-semibold hover:text-primary/80 transition-colors">
          Sign in
        </button>
      </p>
    </div>
  );
}